import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import HERO from "../assets/corporate-building.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(email, password); navigate("/"); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden lg:block bg-[#1D1D1B]">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover object-center opacity-60" data-testid="login-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B] via-[#1D1D1B]/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Logo className="h-10" forceDark />
          <div className="max-w-lg rise">
            <div className="eyebrow !text-white/70 mb-4">EP Investments Ltd</div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">Every JV project, every pound, in one live place.</h1>
            <p className="mt-5 text-white/75">Portfolio cashflows, partner positions and checks – updated the moment anything changes.</p>
          </div>
        </div>
      </aside>
      <main className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden mb-10"><Logo className="h-9" /></div>
          <div className="eyebrow mb-2">JV Hub</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-muted-foreground">Use your EPIL or partner account.</p>
          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
            </div>
            {error && <div role="alert" className="rounded-md border border-primary/40 bg-accent px-3 py-2.5 text-sm font-medium text-accent-foreground" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full text-sm font-semibold" data-testid="login-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Developer or land partner? <Link to="/apply" className="font-semibold text-primary" data-testid="become-partner-link">Become a JV partner</Link></p>
        </form>
      </main>
    </div>
  );
}
